import React, { FC, useEffect, useState } from 'react';
import { Icons } from '@silverfort/sf-components';
import { useCloudResources } from '../../hooks/useCloudResources';
import { getUserState } from '../utils';
import MessagePage from '../Controls/GenericMessagePage/MessagePage';
import { useAppDrawer } from '../AppDrawer/AppDrawerProvider';
import ReportsDrawer from '../ReportsDrawer/ReportsDrawer';

enum CloudFrameStatus {
    LOADED = 'LOADED',
    IS_LOADING = 'IS_LOADING'
}

type MessageEventType = CloudFrameStatus | 'OPEN_REPORT_DRAWER' | 'NAVIGATE_TO_CLOUD_INTEGRATIONS_SETTINGS';
type CloudInventoryComponentData = {
    source: 'CLOUD-INVENTORY-COMPONENT';
    data: { active: boolean; headerHeight: number; menuWidth: number };
};
type MessageEventData = CloudInventoryComponentData;
export type CloudFrameMessageEvent = MessageEvent<MessageEventType | MessageEventData>;

interface CloudFrameProps {
    srcRoute: string | null;
    fallback?: React.ReactNode;
    className?: string;
    silverfortRoute?: string;
    customEventHandler?: (event: CloudFrameMessageEvent) => void;
}

const DefaultFallback: FC = () => (
    <MessagePage
        icon={Icons.CloudOff}
        bigMsg="Partial Connection"
        smallMsgs={[
            'Could not connect you to cloud services.',
            'You can still use Silverfort without cloud abilities.'
        ]}
    />
);

const CloudFrame: FC<CloudFrameProps> = ({
    srcRoute,
    fallback,
    className = '',
    customEventHandler,
    silverfortRoute
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isError, setIsError] = useState(false);
    const [environmentId, setEnvironmentId] = useState<string | null>(null);
    const { cloudResourcesBaseUrl, isCloudResourcesConnected } = useCloudResources();
    const { displayContent } = useAppDrawer();

    useEffect(() => {
        let isMounted = true;
        const fetchUserData = async () => {
            try {
                // @ts-ignore - js-ts problems
                const userData = (await getUserState()) as { user: { environmentId: string } };

                if (isMounted && userData?.user?.environmentId) {
                    setEnvironmentId(userData.user.environmentId);
                }
            } catch (error) {
                console.error("failed getting the user's environmentId from server!", error);
                setIsError(true);
            }
        };

        fetchUserData();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (isCloudResourcesConnected) {
            if (environmentId && srcRoute && cloudResourcesBaseUrl) {
                const handleMessage = (event: CloudFrameMessageEvent) => {
                    if (event.origin === cloudResourcesBaseUrl) {
                        if (event.data === CloudFrameStatus.LOADED || event.data === CloudFrameStatus.IS_LOADING) {
                            setIsLoaded(event.data === CloudFrameStatus.LOADED);
                        } else if (event.data === 'OPEN_REPORT_DRAWER') {
                            displayContent(<ReportsDrawer />);
                        } else {
                            customEventHandler?.(event);
                        }
                    }
                };
                window.addEventListener('message', (event: CloudFrameMessageEvent) => {
                    handleMessage(event);
                });
                return () => {
                    window.removeEventListener('message', handleMessage);
                };
            }
        } else if (isCloudResourcesConnected !== null) {
            setIsLoaded(true);
        }
    }, [isCloudResourcesConnected, environmentId, srcRoute, cloudResourcesBaseUrl, displayContent, customEventHandler]);

    if (isCloudResourcesConnected === false || isError) {
        return fallback ? <>{fallback}</> : <DefaultFallback />;
    }

    return (
        <div className={`relative w-full h-full ${className}`}>
            {!isLoaded && (
                <div className="silverfort-loader">
                    <div className="logo"></div>
                </div>
            )}
            {cloudResourcesBaseUrl && srcRoute && environmentId && (
                <iframe
                    allow="clipboard-write"
                    className="w-full h-full"
                    height="100%"
                    width="100%"
                    src={`${cloudResourcesBaseUrl}${srcRoute}?silverfortOrg=${environmentId}${
                        silverfortRoute ? `&route=${silverfortRoute}` : ''
                    }`}
                    title="CloudFrame-iframe"
                />
            )}
        </div>
    );
};

export default CloudFrame;

export const isCloudMessageEventData = (
    data: MessageEventType | MessageEventData
): data is CloudInventoryComponentData => {
    const eventData = data as MessageEventData;
    return !!eventData?.source;
};
